/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
/* eslint-disable no-plusplus */
/* eslint object-shorthand: "error" */
/* eslint-env es6 */

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useEffectAsync } from "lib/react/customHooks";
import { primaryColor } from "resources/constants";
import { makeStyles, Typography, Box } from "@material-ui/core";
import { AiOutlineShop, AiOutlineTags } from "react-icons/ai";
import { GoLocation } from "react-icons/go";
import { matchQueryFilters } from "lib/utils";
import _ from "lodash";
import moment from "moment";
import CardComponent from "app/common/CardComponent";
import ScaleLoader from "react-spinners/ScaleLoader";
import DemandPlanningChartComponent from "./DemandPlanningChartComponent";
import ExplicabilityComponent from "../explicability-info/ExplicabilityComponent";
import SelectComponent from "../elasticity/SelectComponent";

const useStyles = makeStyles((theme) => ({
  spinnerContainer: {
    display: "flex",
    flexGrow: "1",
    alignItems: "center",
    justifyContent: "center",
  },
  aggregateContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: "5px",
  },
  chartContainer: {
    position: "relative",
    overflow: "hidden",
    flexGrow: "1",
    [theme.breakpoints.up("xs")]: {
      height: "155px",
    },
    [theme.breakpoints.up("sm")]: {
      height: "200px",
    },
    [theme.breakpoints.up("md")]: {
      height: "300px",
    },
    [theme.breakpoints.up("lg")]: {
      height: "100%",
    },
  },
  componentTitle: {
    fontWeight: "bold",
    fontSize: "14px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "16px",
      marginLeft: "22px",
    },
  },
}));

function DemandPlanningCardComponent({
  demandPlanningEstimations,
  historicsData,
}) {
  const classes = useStyles();
  const [data, setData] = useState();
  const filtersSlice = useSelector((state) => state.filters);
  const catalogSlice = useSelector((state) => state.catalog);
  const retailersSlice = useSelector((state) => state.retailers);
  const retailersGroupSlice = useSelector((state) => state.retailersGroup);
  const areasSlice = useSelector((state) => state.areas);
  const [demandData, setDemandData] = useState({
    labels: [],
    datasets: [],
  });
  const [historicData, setHistoricData] = useState(null);
  const [
    filteredAggregatedHistorics,
    setFilteredAggregatedHistorics,
  ] = useState(null);
  const [triplets, setTriplets] = useState(null);
  const [timeSeriesGroupBy, setTimeSeriesGroupBy] = useState({
    key: null,
    label: "Aggregated",
  });
  const selectOptions = [
    {
      key: null,
      label: "Aggregated",
    },
    {
      key: "category_id",
      label: "By Category",
      icon: <AiOutlineTags size={22} />,
    },
    {
      key: "line_id",
      label: "By Line",
      icon: <AiOutlineTags size={22} />,
    },
    {
      key: "brand_id",
      label: "By Brand",
      icon: <AiOutlineTags size={22} />,
    },
    {
      key: "retailer_group_id",
      label: "By Retailer Group",
      icon: <AiOutlineShop size={22} />,
    },
    {
      key: "retailer_id",
      label: "By Retailer",
      icon: <AiOutlineShop size={22} />,
    },
    {
      key: "area_id",
      label: "By Area",
      icon: <GoLocation size={22} />,
    },
  ];

  const retrieveDemand = async (tupleKey) => {
    const queryFilters = {
      ...filtersSlice.queryFilters,
    };

    // Filter the demand and historic data
    const JsonDataDemandFiltered = _.filter(data, (obj) =>
      matchQueryFilters(obj, queryFilters)
    );
    const historic = _.filter(historicData, (obj) =>
      matchQueryFilters(obj, queryFilters)
    );
    // Concat both datasets

    const datasetConcat = historic.concat(JsonDataDemandFiltered);
    // Create the labels for the chart

    const uniqueDemandByDate = _.pickBy(
      _.uniqBy(datasetConcat, "date"),
      _.identity
    );

    let labeledDates = _.map(
      _.sortBy(uniqueDemandByDate, "date", "asc"),
      (demand) => {
        return moment(demand.date).format("MMM'YY");
      }
    );

    // Take the 27 labels to show in the chart and all the demand data in the triplets

    labeledDates = labeledDates.slice(labeledDates.length - 27);

    let demands = _.filter(datasetConcat, (estimation) => {
      const key = `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`;
      return key in triplets;
    });

    if (timeSeriesGroupBy.key) {
      // Group the demand data by the key and delete if there are the data with null values

      demands = _.groupBy(demands, timeSeriesGroupBy.key);

      if (demands.null) {
        delete demands.null;
      }

      demands = _.orderBy(demands, "date", "desc");

      const infoToGroup = _.map(demands, (dataset, index) => {
        return {
          area_id: dataset[index]?.area_id,
          product_id: dataset[index]?.product_id,
          retailer_group_id: dataset[index]?.retailer_group_id,
          retailer_id: dataset[index]?.retailer_id,
          brand_id: dataset[index]?.brand_id,
          line_id: dataset[index]?.line_id,
          category_id: dataset[index]?.category_id,
        };
      });

      const sum_historics = _.map(demands, (dataset) => {
        // Group by date and sum units for each date
        const historics = _.chain(dataset)
          .groupBy("date") // Group the dataset by date
          .map((entries, date) => ({
            date,
            mid_units: _.sumBy(entries, "units"), // Sum up the units for this date
          }))
          .value();

        // Remove the last 24 entries (most recent)
        return _.take(historics, Math.max(historics.length - 24, 0));
      });

      setFilteredAggregatedHistorics(sum_historics);

      const low_unitsArray = _.map(demands, (dataset) => {
        const lowDataPoints = Array.from(
          dataset.reduce(
            (m, { date, low_units }) =>
              m.set(date, (m.get(date) || 0) + low_units),
            new Map()
          ),
          ([date, low_units]) => ({ date, low_units })
        );
        return lowDataPoints.slice(
          lowDataPoints.length - 24,
          lowDataPoints.length
        );
      });

      const mid_unitsArray = _.map(demands, (dataset) => {
        const midDataPoints = Array.from(
          dataset.reduce(
            (m, { date, mid_units }) =>
              m.set(date, (m.get(date) || 0) + mid_units),
            new Map()
          ),
          ([date, mid_units]) => ({ date, mid_units })
        );
        return midDataPoints.slice(
          midDataPoints.length - 24,
          midDataPoints.length
        );
      });

      const high_unitsArray = _.map(demands, (dataset) => {
        const highDataPoints = Array.from(
          dataset.reduce(
            (m, { date, high_units }) =>
              m.set(date, (m.get(date) || 0) + high_units),
            new Map()
          ),
          ([date, high_units]) => ({ date, high_units })
        );
        return highDataPoints.slice(
          highDataPoints.length - 24,
          highDataPoints.length
        );
      });

      const sum_demand = _.merge(
        low_unitsArray,
        mid_unitsArray,
        high_unitsArray
      );

      demands = _.map(sum_demand, (dataset, index) => {
        return _.concat(sum_historics[index], dataset);
      });

      demands = _.map(demands, (dataset, index) => {
        return _.map(dataset, (dataPoint) => {
          return { ...dataPoint, ...infoToGroup[index] };
        });
      });

      demands = _.groupBy(demands.flat(), timeSeriesGroupBy.key);
    } else {
      const low_unitsArray = Array.from(
        JsonDataDemandFiltered.reduce(
          (m, { date, low_units }) =>
            m.set(date, (m.get(date) || 0) + low_units),
          new Map()
        ),
        ([date, low_units]) => ({ date, low_units })
      );

      const mid_unitsArray = Array.from(
        JsonDataDemandFiltered.reduce(
          (m, { date, mid_units }) =>
            m.set(date, (m.get(date) || 0) + mid_units),
          new Map()
        ),
        ([date, mid_units]) => ({ date, mid_units })
      );

      const high_unitsArray = Array.from(
        JsonDataDemandFiltered.reduce(
          (m, { date, high_units }) =>
            m.set(date, (m.get(date) || 0) + high_units),
          new Map()
        ),
        ([date, high_units]) => ({ date, high_units })
      );

      let sum_historics = Array.from(
        historic.reduce(
          (m, { date, units }) => m.set(date, (m.get(date) || 0) + units),
          new Map()
        ),
        ([date, mid_units]) => ({ date, mid_units })
      );

      sum_historics = _.sortBy(sum_historics, "date", "desc");
      sum_historics[sum_historics.length - 1] = {
        ...sum_historics[sum_historics.length - 1],
        low_units: sum_historics[sum_historics.length - 1]?.mid_units,
        high_units: sum_historics[sum_historics.length - 1]?.mid_units,
      };

      demands = _.sortBy(
        sum_historics?.concat(
          _.merge(low_unitsArray, mid_unitsArray, high_unitsArray)
        ),
        "date",
        "desc"
      );

      setFilteredAggregatedHistorics(demands.slice(0, demands.length - 24));
    }

    return {
      key: tupleKey,
      timeSeries: demands,
      labeledDates,
    };
  };

  const getLabelForGroupBy = (itemId) => {
    if (itemId === "null") return ""; // There are null values coming from the database and the lodash groupBy places them into the "null" property
    switch (timeSeriesGroupBy.key) {
      case "retailer_id":
        return `${retailersSlice[itemId]?.label}`;
      case "area_id":
        return `${areasSlice[itemId]?.label}`;
      case "brand_id":
        return `${catalogSlice.brands[itemId]?.label}`;
      case "line_id":
        return `${catalogSlice.lines[itemId]?.label}`;
      case "category_id":
        return `${catalogSlice.categories[itemId]?.label}`;
      case "retailer_group_id":
        return `${retailersGroupSlice[itemId]?.label}`;
      default:
        return "Aggregated";
    }
  };

  const getDatasetFromDemandTimeSeries = (
    demandTimeSeries,
    timeSeriesCatalogItemId,
    alpha
  ) => {
    if (!timeSeriesGroupBy.key) {
      return [
        {
          label: `Optimistic Case`,
          type: "line",
          fill: 1,
          borderColor: `#FFB26B`,
          pointBordercolor: `rgba(155, 155, 155,${alpha})`,
          pointBackgroundColor: "#ffffff",
          backgroundColor: `#FFF0E2`,
          hoverBorderWidth: 3,
          hoverBorderColor: "#FFB26B",
          hoverBackgroundColor: `#FFF0E2`,
          pointHoverBackgroundColor: "#ffffff",
          pointHoverRadius: 6,
          data: _.map(
            demandTimeSeries.slice(demandTimeSeries.length - 27),
            (demand) => {
              return {
                x: moment(demand.date).format("MMM'YY"),
                y: demand.high_units,
                label: `Optimistic Case`,
                high_units: demand.high_units,
              };
            }
          ),
        },
        {
          label: `Probable Case`,
          type: "line",
          fill: false,
          borderColor: `rgba(166, 166, 166, 1)`,
          pointBordercolor: `rgba(166, 166, 166, 1)`,
          pointBackgroundColor: "#ffffff",
          backgroundColor: `rgba(166, 166, 166,${alpha})`,
          hoverBorderWidth: 3,
          hoverBorderColor: `rgba(166, 166, 166, 1)`,
          pointHoverBackgroundColor: "#ffffff",
          pointHoverRadius: 6,
          data: _.map(
            demandTimeSeries.slice(demandTimeSeries.length - 27),
            (demand) => {
              return {
                x: moment(demand.date).format("MMM'YY"),
                y: demand.mid_units,
                label: `Probable Case`,
                mid_units: demand.mid_units,
              };
            }
          ),
        },
        {
          label: `Worse Case`,
          type: "line",
          fill: 1,
          borderColor: `rgba(74, 175, 223, 1)`,
          pointBordercolor: `rgba(74, 175, 223, 1)`,
          pointBackgroundColor: "#ffffff",
          backgroundColor: `rgba(206, 234, 248,${alpha})`,
          hoverBorderWidth: 3,
          hoverBorderColor: `rgba(74, 175, 223, 1)`,
          pointHoverBackgroundColor: "#ffffff",
          pointHoverRadius: 6,
          data: _.map(
            demandTimeSeries.slice(demandTimeSeries.length - 27),
            (demand) => {
              return {
                x: moment(demand.date).format("MMM'YY"),
                y: demand.low_units,
                label: `Worse Case`,
                low_units: demand.low_units,
              };
            }
          ),
        },
      ];
    }

    let sliceIndex = -27;
    if (
      demandTimeSeries.length === 35 &&
      filtersSlice.filtersKey ===
        "undefined-undefined-undefined-undefined-undefined-undefined-undefined"
    ) {
      sliceIndex = -26;
    }
    return {
      label: `${getLabelForGroupBy(timeSeriesCatalogItemId)}`,
      type: "line",
      fill: false,
      borderColor: `rgba(166, 166, 166, ${alpha})`,
      pointBordercolor: `rgba(166, 166, 166, ${alpha})`,
      pointBackgroundColor: "#ffffff",
      backgroundColor: `rgba(166, 166, 166, ${alpha})`,
      hoverBorderWidth: 3,
      hoverBorderColor: `rgba(166, 166, 166, ${alpha})`,
      pointHoverBackgroundColor: "#ffffff",
      hoverBorderDash: [4, 3],
      data: _.map(
        Object.entries(demandTimeSeries)
          .slice(sliceIndex)
          .map((entry) => entry[1]),
        (demand) => {
          return {
            x: moment(demand.date).format("MMM'YY"),
            y: demand.mid_units,
            label: `${getLabelForGroupBy(timeSeriesCatalogItemId)}`,
            mid_units: demand.mid_units,
            low_units: demand.low_units,
            high_units: demand.high_units,
          };
        }
      ),
    };
  };

  useEffect(() => {
    const dataWithIsProductClient = _.filter(historicsData, (historic) => {
      return (
        catalogSlice.products[historic.product_id].is_product_client === true
      );
    });
    const historicDataWithCatalog = _.map(
      dataWithIsProductClient,
      (historic) => {
        return {
          ...historic,
          brand_id: catalogSlice.products[historic.product_id]?.brand_id,
          retailer_group_id:
            retailersSlice[historic.retailer_id]?.retailer_group_id,
          line_id: catalogSlice.products[historic.product_id]?.line_id,
          category_id:
            catalogSlice.products[historic.product_id]?.line.categories[0].id,
        };
      }
    );

    setHistoricData(historicDataWithCatalog);

    const demandDataWithCatalog = _.map(demandPlanningEstimations, (demand) => {
      return {
        ...demand,
        brand_id: catalogSlice.products[demand.product_id]?.brand_id,
        retailer_group_id:
          retailersSlice[demand.retailer_id]?.retailer_group_id,
        line_id: catalogSlice.products[demand.product_id]?.line_id,
        category_id:
          catalogSlice.products[demand.product_id]?.line.categories[0].id,
      };
    });

    setData(demandDataWithCatalog);

    // Create the triplets keys with the merge of the demand and historic triplets keys

    const uniqueTriplets = _.uniqBy(demandPlanningEstimations, (estimation) => {
      return [
        estimation.product_id,
        estimation.area_id,
        estimation.retailer_id,
      ].join();
    });

    const uniqueTripletsKeyDemand = _.keyBy(
      uniqueTriplets,
      (estimation) =>
        `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
    );

    const uniqueTripletsHistoric = _.uniqBy(historicsData, (estimation) => {
      return [
        estimation.product_id,
        estimation.area_id,
        estimation.retailer_id,
      ].join();
    });

    const uniqueTripletsKeyHistoric = _.keyBy(
      uniqueTripletsHistoric,
      (estimation) =>
        `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
    );

    const tripletsInCommom = _.merge(
      uniqueTripletsKeyHistoric,
      uniqueTripletsKeyDemand
    );
    setTriplets(tripletsInCommom);
  }, []);

  useEffectAsync(
    async () => {
      const tupleKey = `timeseries-${filtersSlice.filtersKey}-${timeSeriesGroupBy.key}`;
      return retrieveDemand(tupleKey);
    },
    (result) => {
      setDemandData({
        labels: result.labeledDates,
        datasets: getDatasetFromDemandTimeSeries(result.timeSeries, null, 1),
      });
    },
    [triplets]
  );

  useEffectAsync(
    async () => {
      const tupleKey = `timeseries-${filtersSlice.filtersKey}-${timeSeriesGroupBy.key}`;
      return retrieveDemand(tupleKey);
    },

    (result) => {
      const totalDatasetsCount = _.size(
        _.pickBy(result.timeSeries, (value, key) => key !== "null")
      );
      let index = 0;
      setDemandData({
        labels: result.labeledDates,
        datasets: timeSeriesGroupBy.key // If there are groupBy generate the dataset with the group
          ? _.map(
              result.timeSeries,
              (demandTimeSeries, timeSeriesCatalogItemId) => {
                index += 1;
                return getDatasetFromDemandTimeSeries(
                  demandTimeSeries,
                  timeSeriesCatalogItemId,
                  index / totalDatasetsCount + 0.25 // Alpha value for the opacity of this dataset in the chart
                );
              }
            )
          : getDatasetFromDemandTimeSeries(result.timeSeries, null, 1),
      });
    },
    [filtersSlice.filtersKey, timeSeriesGroupBy.key]
  );

  return demandData.labels.length !== 0 || demandData.datasets ? (
    <CardComponent
      title={
        <>
          <Typography variant="body1" className={classes.componentTitle}>
            Sellout
            <ExplicabilityComponent
              tooltipText={
                <>
                  See the predictive sellout volume with its uncertainty band
                  for the next 24 months compared to the past. The{" "}
                  <strong>Probable Case </strong>
                  is calculated using the pricing strategy seen during the past
                  year
                </>
              }
            />
          </Typography>
          <div className={classes.aggregateContainer}>
            <SelectComponent
              selectValue={timeSeriesGroupBy}
              setSelectValue={setTimeSeriesGroupBy}
              selectTitle="Aggregation"
              selectOptions={selectOptions}
            />
          </div>
        </>
      }
      body={
        <div className={classes.chartContainer}>
          <DemandPlanningChartComponent
            groupBy={timeSeriesGroupBy}
            setGroupBy={setTimeSeriesGroupBy}
            chartData={demandData}
            filteredAggregatedHistorics={filteredAggregatedHistorics}
          />
        </div>
      }
    />
  ) : (
    <Box component="div" className={classes.spinnerContainer}>
      <ScaleLoader size={150} color={primaryColor} loading />
    </Box>
  );
}

export default DemandPlanningCardComponent;
